import type { BriefingJournalRow, BriefingJournalSignature } from '@railroute/contracts/core';
import { journalDate } from './journal';

// Экранный вид журнала инструктажа (ночная проверка стенда 21.10.2026).
//
// ЭКРАН — НЕ БЛАНК. Печать (PDF) и Excel по-прежнему строятся из граф
// приложения 17 (`journal.ts`) и не меняются ни буквой. Экрану бланк не
// нужен: десять граф с заголовками в шесть строк и «ключ устройства, без
// ЭЦП» в каждой ячейке человек не читает. Здесь — то же содержание, собранное
// для чтения: шесть колонок, «не прошёл» видно сразу, пометки о способе
// подписи — одним абзацем под таблицей.
//
// Модуль ЧИСТЫЙ: ни сети, ни разметки — счётчики, отборы и подписи словами
// проверяются тестами.

export type JournalKindFilter = 'ALL' | BriefingJournalRow['briefingKind'];

/** Кнопки отбора по виду — в этом порядке. */
export const KIND_FILTERS: readonly { value: JournalKindFilter; label: string }[] = [
  { value: 'ALL', label: 'Все' },
  { value: 'WORKPLACE_INITIAL', label: 'Первичный' },
  { value: 'REPEATED', label: 'Повторный' },
  { value: 'UNSCHEDULED', label: 'Внеплановый' },
  { value: 'TARGET', label: 'Целевой' },
];

/** Значок вида: короткое слово и цвет (первичный — синий, повторный — серый, внеплановый — жёлтый, целевой — фиолетовый). */
export const KIND_BADGE: Record<BriefingJournalRow['briefingKind'], { label: string; tone: 'blue' | 'grey' | 'yellow' | 'violet' }> = {
  WORKPLACE_INITIAL: { label: 'Первичный', tone: 'blue' },
  REPEATED: { label: 'Повторный', tone: 'grey' },
  UNSCHEDULED: { label: 'Внеплановый', tone: 'yellow' },
  TARGET: { label: 'Целевой', tone: 'violet' },
};

/** Работник прошёл проверку, а его подписи ещё нет — ждёт подписи. */
export function awaitsWorker(row: BriefingJournalRow): boolean {
  return row.knowledgePassed !== false && row.workerSignature === null;
}

export function failed(row: BriefingJournalRow): boolean {
  return row.knowledgePassed === false;
}

export function signedOnPaper(row: BriefingJournalRow): boolean {
  return row.workerSignature?.method === 'PAPER_JOURNAL';
}

/** Четыре карточки над таблицей — за весь период, без учёта отборов. */
export function journalCounts(rows: readonly BriefingJournalRow[]) {
  return {
    total: rows.length,
    failed: rows.filter(failed).length,
    awaitingWorker: rows.filter(awaitsWorker).length,
    onPaper: rows.filter(signedOnPaper).length,
  };
}

/** Счётчики на кнопках отбора по виду. */
export function kindCounts(rows: readonly BriefingJournalRow[]): Record<JournalKindFilter, number> {
  const counts: Record<JournalKindFilter, number> = { ALL: rows.length, WORKPLACE_INITIAL: 0, REPEATED: 0, UNSCHEDULED: 0, TARGET: 0 };
  for (const row of rows) counts[row.briefingKind] += 1;
  return counts;
}

export interface JournalScreenFilter {
  kind: JournalKindFilter;
  onlyFailed: boolean;
  onlyUnsigned: boolean;
  /** Поиск по ФИО работника или проводившего. */
  query: string;
}

export const NO_FILTER: JournalScreenFilter = { kind: 'ALL', onlyFailed: false, onlyUnsigned: false, query: '' };

/**
 * Строки экрана: отборы и порядок «новые сверху». Сервер отдаёт журнал в
 * порядке журнала (старые первыми — так и печатается); экран его
 * переворачивает, а при равной дате сохраняет обратный порядок записи.
 */
export function screenRows(rows: readonly BriefingJournalRow[], filter: JournalScreenFilter): BriefingJournalRow[] {
  const query = filter.query.trim().toLocaleLowerCase('ru');
  return rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => filter.kind === 'ALL' || row.briefingKind === filter.kind)
    .filter(({ row }) => !filter.onlyFailed || failed(row))
    .filter(({ row }) => !filter.onlyUnsigned || awaitsWorker(row))
    .filter(
      ({ row }) =>
        query === '' ||
        row.briefedName.toLocaleLowerCase('ru').includes(query) ||
        (row.conductorLabel ?? '').toLocaleLowerCase('ru').includes(query),
    )
    .sort((a, b) => (a.row.date === b.row.date ? b.index - a.index : a.row.date < b.row.date ? 1 : -1))
    .map(({ row }) => row);
}

/** Под ФИО работника: «должность · 1999 г. р.» — из того, что заполнено. */
export function workerSubline(row: BriefingJournalRow): string {
  return [row.professionAndPosition, row.birthYear === null ? null : `${row.birthYear} г. р.`].filter(Boolean).join(' · ');
}

/**
 * «Фамилия И. О., должность» (графа 7) — на две строки: имя и должность.
 * Должность отделена первой запятой: в самих инициалах запятой не бывает.
 */
export function conductorLines(row: BriefingJournalRow): { name: string; position: string | null } {
  const label = row.conductorLabel ?? '';
  const comma = label.indexOf(', ');
  if (comma < 0) return { name: label, position: null };
  return { name: label.slice(0, comma), position: label.slice(comma + 2) };
}

/** Под значком вида: причина внепланового или наряд целевого. */
export function kindNote(row: BriefingJournalRow): { reason: string | null; workOrderNumber: string | null } {
  return { reason: row.unscheduledReason, workOrderNumber: row.briefingKind === 'TARGET' ? row.workOrderNumber : null };
}

export type SignatureLine = { text: string; tone: 'ok' | 'warn' | 'muted'; paperMarkId: string | null };

function signed(signature: BriefingJournalSignature, who: string): SignatureLine {
  switch (signature.method) {
    case 'PAPER_JOURNAL':
      return { text: `✓ ${who} · бумага, фото`, tone: 'ok', paperMarkId: signature.paperMarkId };
    case 'UNSIGNED_MARK':
      return { text: `${who} — отметка без подписи`, tone: 'muted', paperMarkId: null };
    case 'PROXY_WITHDRAWN':
      return { text: `${who} — отметка за работника отменена`, tone: 'muted', paperMarkId: null };
    default:
      return { text: `✓ ${who}`, tone: 'ok', paperMarkId: null };
  }
}

/** Колонка «Подписи»: две строки — проводившего и работника. */
export function signatureLines(row: BriefingJournalRow): [SignatureLine, SignatureLine] {
  const conductor = signed(row.conductorSignature, 'Провёл');
  if (row.workerSignature) return [conductor, signed(row.workerSignature, 'Работник')];
  // «Не прошёл» работник не подписывает: подписывать нечего, нужен повторный.
  if (failed(row)) return [conductor, { text: 'Работник — не подписывает', tone: 'muted', paperMarkId: null }];
  return [conductor, { text: 'Работник — ждёт подписи', tone: 'warn', paperMarkId: null }];
}

/**
 * Абзац под таблицей вместо пометок в каждой ячейке: каким способом
 * поставлены подписи и какие наряды выданы без ЭЦП. Пусто — абзаца нет.
 */
export function signatureFootnote(rows: readonly BriefingJournalRow[]): string[] {
  const all = rows.flatMap((row) => [row.conductorSignature, row.workerSignature]).filter((s): s is BriefingJournalSignature => s !== null);
  const notes: string[] = [];
  if (all.some((s) => s.method === 'DEVICE_KEY')) {
    notes.push('Подписи «✓» поставлены в приложении ключом устройства — это подпись без ЭЦП.');
  }
  if (all.some((s) => s.method === 'QUALIFIED_ECP')) {
    notes.push('Часть подписей поставлена ЭЦП — в печатной форме они так и названы.');
  }
  if (all.some((s) => s.method === 'PAPER_JOURNAL')) {
    notes.push('«Бумага, фото» — работник расписался в бумажном журнале, в системе фото страницы.');
  }
  const unsignedOrders = [...new Set(rows.filter((r) => r.issueUnsigned && r.workOrderNumber).map((r) => r.workOrderNumber as string))];
  if (unsignedOrders.length > 0) {
    notes.push(`Наряд${unsignedOrders.length > 1 ? 'ы' : ''} № ${unsignedOrders.join(', ')} выдан${unsignedOrders.length > 1 ? 'ы' : ''} без ЭЦП выдающего; подписи целевого инструктажа по ${unsignedOrders.length > 1 ? 'ним' : 'нему'} опираются на неподписанный документ выдачи.`);
  } else if (rows.some((r) => r.issueUnsigned)) {
    notes.push('Часть целевых инструктажей проведена по наряду, выданному без ЭЦП выдающего.');
  }
  return notes;
}

/** Дата строки — `ДД.ММ.ГГГГ`, как в печатной форме. */
export const screenDate = journalDate;
